/**
 * AppRoutes
 * 애플리케이션 라우팅 설정
 */
import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import LoginPage from "@/features/auth/components/pages/LoginPage/LoginPage";
import RegisterSelectPage from "@/features/auth/components/pages/RegisterSelectPage/RegisterSelectPage";
import HeadquartersRegisterPage from "@/features/auth/components/pages/HeadquartersRegisterPage/HeadquartersRegisterPage";
import SupplierRegisterPage from "@/features/auth/components/pages/SupplierRegisterPage/SupplierRegisterPage";
import ForgotPasswordPage from "@/features/auth/components/pages/ForgotPasswordPage/ForgotPasswordPage";
import ResetPasswordPage from "@/features/auth/components/pages/ResetPasswordPage/ResetPasswordPage";
import DashboardPage from "@/features/dashboard/components/pages/DashboardPage/DashboardPage";
import ProtectedRoute from "./routes/ProtectedRoute";
import PublicRoute from "./routes/PublicRoute";
import { getItem } from "@/shared/utils/storage";

/**
 * 뒤로가기 이벤트를 처리하는 컴포넌트
 */
const BackButtonHandler: React.FC = () => {
    const navigate = useNavigate();

    useEffect(() => {
        const handlePopState = () => {
            const currentUserId = getItem<string>('USER_ID');
            
            // 인증된 사용자가 뒤로가기를 누른 경우
            if (currentUserId) {
                setTimeout(() => {
                    const currentPath = window.location.pathname;
                    
                    // 로그인 페이지나 공개 페이지로 가려고 하면 대시보드로 리다이렉트
                    if (currentPath === '/login' || currentPath.startsWith('/register') || 
                        currentPath === '/forgot-password' || currentPath === '/reset-password') {
                        navigate('/dashboard', { replace: true });
                    }
                }, 0);
            }
        };

        // popstate 이벤트 리스너 등록
        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [navigate]);

    return null;
};

const RoutesContent: React.FC = () => {
    return (
        <>
            <BackButtonHandler />
            <Routes>
                {/* 공개 페이지 (인증되지 않은 사용자만 접근) */}
                <Route path="/login" element={
                    <PublicRoute>
                        <LoginPage />
                    </PublicRoute>
                } />

                {/* 비밀번호 찾기 */}
                <Route path="/forgot-password" element={
                    <PublicRoute>
                        <ForgotPasswordPage />
                    </PublicRoute>
                } />

                {/* 비밀번호 재설정 */}
                <Route path="/reset-password" element={
                    <PublicRoute>
                        <ResetPasswordPage />
                    </PublicRoute>
                } />

                {/* 회원가입 페이지 */}
                <Route path="/register" element={
                    <PublicRoute>
                        <RegisterSelectPage />
                    </PublicRoute>
                } />
                <Route path="/register/headquarters" element={
                    <PublicRoute>
                        <HeadquartersRegisterPage />
                    </PublicRoute>
                } />
                <Route path="/register/supplier" element={
                    <PublicRoute>
                        <SupplierRegisterPage />
                    </PublicRoute>
                } />

                {/* 루트 경로는 인증 상태에 따라 리다이렉트 */}
                <Route path="/" element={<Navigate to="/dashboard" replace />} />

                {/* 보호된 페이지 (인증이 필요한 페이지) */}
                <Route path="/dashboard" element={
                    <ProtectedRoute>
                        <DashboardPage />
                    </ProtectedRoute>
                } />

                {/* 404페이지 - 인증된 사용자는 대시보드로, 미인증 사용자는 로그인으로 */}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
        </>
    );
};

const AppRoutes: React.FC = () => {
    return (
        <BrowserRouter>
            <RoutesContent />
        </BrowserRouter>
    );
};

export default AppRoutes;
